'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { useState } from 'react';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { BlogCard } from './blog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const ALL_CATEGORIES_TAB = 'all';

export const BlogsList = ({
  locale,
  categories,
}: {
  locale: string;
  categories: { id: number; name: string }[];
}) => {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 7,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any, pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <Tabs
      value={activeTab}
      defaultValue={activeTab}
      onValueChange={value => setActiveTab(value)}
      className={cn('custom-container', 'flex flex-col items-start justify-start gap-8')}
    >
      <TabsList
        className={cn(
          'hide-scroll-bar',
          'flex h-fit w-full flex-nowrap items-start justify-start gap-2 overflow-scroll rounded-none bg-transparent py-0',
        )}
      >
        <TabsTrigger
          value={ALL_CATEGORIES_TAB}
          className={cn(
            'ea-blogs__tabs-trigger',
            'rounded-[12px] px-6 py-4 shadow-none lg:py-[18px]',
          )}
        >
          {t('text1947')}
        </TabsTrigger>
        {categories.map(category => (
          <TabsTrigger
            value={String(category.id)}
            key={category.id}
            className={cn(
              'ea-blogs__tabs-trigger',
              'rounded-[12px] px-6 py-4 shadow-none transition-all lg:py-[18px]',
            )}
          >
            {category.name}
          </TabsTrigger>
        ))}
      </TabsList>
      {[ALL_CATEGORIES_TAB, ...categories.map(category => String(category.id))].map(tabValue => (
        <TabsContent key={tabValue} value={tabValue} className="w-full pr-6">
          {tabValue === activeTab && (
            <>
              {blogsQuery.error && <p className="pt-8 ">Vyskytla sa chyba.</p>}
              {blogsQuery.isSuccess && blogs.length === 0 && (
                <p className="w-full text-center font-medium">{t('text752')}</p>
              )}
              {/* {blogsQuery.isLoading && <p className="pt-8 ">Načítavam...</p>} */}
              {blogsQuery.isSuccess && blogs.length > 0 && (
                <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                  {blogs.map((blog, index) => (
                    <BlogCard key={blog.id} blog={blog} firstBlog={index === 0} headingTag="h2" />
                  ))}
                </div>
              )}
              <div
                className={cn('mt-8 flex w-full items-center justify-center', {
                  hidden: !blogsQuery.hasNextPage,
                })}
              >
                <Button
                  onClick={() => blogsQuery.fetchNextPage()}
                  isLoading={blogsQuery.isFetchingNextPage}
                  className="w-full sm:w-fit"
                  variant={'dark'}
                >
                  {t('text338')}
                </Button>
              </div>
            </>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
};

// const ReferencesSkeleton = () => {
//   return (
//     <div
//       className={cn(
//         'animate-pulse',
//         'col-span-1 grid w-full grid-cols-1 gap-8 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3 lg:gap-10',
//       )}
//     >
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//     </div>
//   );
// };
